/**
 * Fumee Global Cart Manager
 *
 * The custom add-to-cart paths (product bundle tiers, cart upsell, quick add)
 * POST straight to /cart/add.js, which tells the rest of the theme nothing. This
 * puts them back on the theme's own update path: fetch the cart sections, hand
 * them to the components through the `cart:update` event they already listen
 * for, and let them morph themselves.
 *
 * It deliberately does not touch innerHTML any more. Replacing the drawer's
 * markup wholesale tore down and rebuilt every custom element inside it, which
 * is what made the drawer flicker and lose its quantity controls on mobile.
 *
 * On timing. The custom paths used to add, then go back for the cart and the
 * rendered sections, and only open the drawer once all of that had landed:
 * measured on the live store, 702ms for the add and another 393ms for the
 * sections, so about 1.1s of a button that looked like it had not been pressed.
 * Two things fix that, and both are in `add` below: the add request now asks for
 * the sections in the same round trip, and the drawer opens before the request
 * goes out rather than after it comes back, with a pending state covering the
 * gap. The server is not faster; the shopper stops waiting on it alone.
 */

(() => {
  const CART_UPDATE_EVENT = 'cart:update';
  const PENDING_ATTR = 'data-fumee-pending';

  const root = () => window.Shopify?.routes?.root ?? '/';

  /**
   * Every cart section on the page. Usually just the drawer; the cart page adds
   * its own.
   * @returns {string[]}
   */
  const cartSectionIds = () => {
    const ids = new Set();

    for (const element of document.querySelectorAll('cart-items-component')) {
      const id = element instanceof HTMLElement ? element.dataset.sectionId : null;
      if (id) ids.add(id);
    }

    return [...ids];
  };

  /**
   * Renders the given sections through the Section Rendering API.
   * @param {string[]} ids
   * @returns {Promise<Record<string, string>>}
   */
  const fetchSections = async (ids) => {
    if (!ids.length) return {};

    const path = window.location.pathname;
    const url = `${path}?sections=${encodeURIComponent(ids.join(','))}&sections_url=${encodeURIComponent(path)}`;
    const response = await fetch(url);

    if (!response.ok) throw new Error(`Section render failed: ${response.status}`);

    return response.json();
  };

  const openDrawer = () => {
    const drawer = document.querySelector('cart-drawer-component');
    if (!drawer) return;

    if (typeof drawer.open === 'function') {
      drawer.open();
      return;
    }

    // Fallback for a drawer whose custom element has not upgraded yet. It has to
    // honour `non-modal` too, or this path would quietly reinstate the blocking
    // overlay the component was changed to drop.
    const dialog = drawer.querySelector('dialog');
    if (!dialog || dialog.open) return;

    if (drawer.hasAttribute('non-modal') && typeof dialog.show === 'function') {
      dialog.show();
    } else if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    }
  };

  /**
   * Marks the drawer busy. Counted rather than set, so two adds in quick
   * succession do not have the first one's finish clear the second one's state.
   */
  let pendingDepth = 0;

  /** @param {boolean} on */
  const setPending = (on) => {
    pendingDepth = Math.max(0, pendingDepth + (on ? 1 : -1));

    for (const drawer of document.querySelectorAll('cart-drawer-component')) {
      if (pendingDepth > 0) drawer.setAttribute(PENDING_ATTR, '');
      else drawer.removeAttribute(PENDING_ATTR);
    }
  };

  /** @type {Promise<void> | null} */
  let inFlight = null;
  let queued = false;
  let queuedOpen = false;

  /**
   * @param {boolean} shouldOpenDrawer
   * @param {Record<string, string> | undefined} presetSections
   */
  const run = async (shouldOpenDrawer, presetSections) => {
    const [cart, sections] = await Promise.all([
      fetch(`${root()}cart.js`).then((response) => response.json()),
      // `add` already has the rendered sections from its own response, so there
      // is nothing to go and fetch. Only a refresh with no sections of its own
      // pays for a second render.
      presetSections
        ? Promise.resolve(presetSections)
        : fetchSections(cartSectionIds()).catch((error) => {
            // A failed section render should not stop the item count updating.
            console.error('Fumee cart: section render failed', error);
            return {};
          }),
    ]);

    // `itemCount` is treated as an absolute total for any source other than
    // 'product-form-component', which is why the source is named here. The
    // sections travel with the event so components morph synchronously, before
    // the drawer animates open, rather than showing stale contents first.
    document.dispatchEvent(
      new CustomEvent(CART_UPDATE_EVENT, {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: 'fumee-cart',
          data: {
            source: 'fumee-cart',
            itemCount: cart.item_count,
            sections,
          },
        },
      })
    );

    if (shouldOpenDrawer) openDrawer();
  };

  window.FumeeCart = {
    /**
     * Adds line items and syncs every cart surface.
     *
     * One round trip: the sections come back with the add. The drawer opens
     * first and wears a pending state until the contents land, so the wait
     * happens inside an open drawer rather than in front of a button that looks
     * inert.
     *
     * @param {Array<{ id: number, quantity: number }>} items
     * @param {{ openDrawer?: boolean }} [options]
     * @returns {Promise<any>}
     */
    async add(items, options = {}) {
      const shouldOpenDrawer = options.openDrawer !== false;
      const ids = cartSectionIds();

      setPending(true);
      if (shouldOpenDrawer) openDrawer();

      try {
        /** @type {Record<string, unknown>} */
        const body = { items };

        if (ids.length) {
          body.sections = ids.join(',');
          body.sections_url = window.location.pathname;
        }

        const response = await fetch(`${root()}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });

        if (!response.ok) throw new Error(`Add to cart failed: ${response.status}`);

        const result = await response.json();

        await this.refresh({ openDrawer: false, sections: result.sections });

        return result;
      } finally {
        setPending(false);
      }
    },

    /**
     * Syncs every cart surface with the server.
     *
     * Overlapping calls are coalesced into one trailing refresh rather than
     * dropped, so the last add always ends up reflected.
     *
     * @param {{ openDrawer?: boolean, sections?: Record<string, string> }} [options]
     * @returns {Promise<void>}
     */
    refresh(options = {}) {
      const shouldOpenDrawer = options.openDrawer !== false;

      if (inFlight) {
        queued = true;
        queuedOpen = queuedOpen || shouldOpenDrawer;
        return inFlight;
      }

      inFlight = run(shouldOpenDrawer, options.sections)
        .catch((error) => {
          console.error('Fumee cart: refresh failed', error);
        })
        .finally(() => {
          inFlight = null;

          if (queued) {
            queued = false;
            const openNext = queuedOpen;
            queuedOpen = false;
            // Deliberately without sections: the ones we were handed described
            // the cart as it was an add ago and are stale by now.
            this.refresh({ openDrawer: openNext });
          }
        });

      return inFlight;
    },
  };

  document.addEventListener('fumee:cart:refresh', () => window.FumeeCart.refresh({ openDrawer: true }));

  // The theme's own add-to-cart form does its work in one round trip already,
  // but it still opens the drawer only once the response lands — about 700ms of
  // a button that looks unpressed. Opening on submit costs nothing if the add
  // succeeds and, if it fails, leaves an open drawer showing the cart as it
  // actually is, with the form's own error beneath it.
  //
  // Capture on the document, so a handler that stops propagation higher up (the
  // bundle tier interception binds on `window`) has already taken the submit and
  // will open the drawer itself through `add`.
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!/\/cart\/add/.test(form.getAttribute('action') || '')) return;

      // The quick-add modal is a dialog of its own; opening the cart drawer over
      // the top of it is not an improvement.
      if (form.closest('dialog')) return;

      const drawer = document.querySelector('cart-drawer-component[auto-open]');
      if (!drawer) return;

      setPending(true);
      openDrawer();

      // The theme dispatches its own cart:update when the add lands. Whichever
      // arrives, the pending state ends; the timeout is the backstop for an add
      // that fails outright and never dispatches anything.
      const clear = () => {
        document.removeEventListener(CART_UPDATE_EVENT, clear);
        clearTimeout(timer);
        setPending(false);
      };
      const timer = setTimeout(clear, 8000);
      document.addEventListener(CART_UPDATE_EVENT, clear);
    },
    { capture: true }
  );
})();
